import { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';

export function InstallPrompt() {
  const { isInstallable, promptInstall, dismissPrompt } = useInstallPrompt();
  const [showPrompt, setShowPrompt] = useState(false);
  const [showManualPrompt, setShowManualPrompt] = useState(false);

  useEffect(() => {
    // Check if user dismissed recently (within 30 days)
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 30) {
        return;
      }
    }

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;

    if (isStandalone || isIOSStandalone) {
      return;
    }

    // Show prompt after 3 seconds
    const timer = setTimeout(() => {
      if (isInstallable) {
        setShowPrompt(true);
      } else {
        // Show manual install prompt for iOS and other browsers
        setShowManualPrompt(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isInstallable]);

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowManualPrompt(false);
    dismissPrompt();
  };

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  if (showPrompt && isInstallable) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in slide-in-from-bottom-4 duration-300">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <Download className="w-8 h-8 text-white" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Install Prayer Times App
            </h2>

            <p className="text-gray-600 mb-6">
              Add this app to your home screen for quick access to prayer times, notifications, and offline support.
            </p>

            <div className="flex flex-col gap-3 w-full">
              <button
                onClick={handleInstall}
                className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-lg hover:shadow-xl"
              >
                Install App
              </button>

              <button
                onClick={handleDismiss}
                className="w-full text-gray-600 px-6 py-2 rounded-xl font-medium hover:bg-gray-100 transition-colors"
              >
                Maybe Later
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              Works offline • No app store required
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (showManualPrompt) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative animate-in fade-in slide-in-from-bottom-4 duration-300">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <Download className="w-8 h-8 text-white" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Install Prayer Times App
            </h2>

            <p className="text-gray-600 mb-4">
              Add this app to your home screen for quick access to prayer times, notifications, and offline support.
            </p>

            {isIOS && isSafari ? (
              <div className="bg-emerald-50 rounded-xl p-4 mb-4 text-left w-full">
                <p className="font-semibold text-emerald-900 mb-2">On iOS Safari:</p>
                <ol className="text-sm text-emerald-800 space-y-1 list-decimal list-inside">
                  <li>Tap the Share button (box with arrow)</li>
                  <li>Scroll down and tap "Add to Home Screen"</li>
                  <li>Tap "Add" to install</li>
                </ol>
              </div>
            ) : (
              <div className="bg-emerald-50 rounded-xl p-4 mb-4 text-left w-full">
                <p className="font-semibold text-emerald-900 mb-2">How to install:</p>
                <ol className="text-sm text-emerald-800 space-y-1 list-decimal list-inside">
                  <li>Tap the browser menu (⋮ or ⋯)</li>
                  <li>Look for "Add to Home Screen" or "Install App"</li>
                  <li>Tap to install</li>
                </ol>
              </div>
            )}

            <button
              onClick={handleDismiss}
              className="w-full text-gray-600 px-6 py-2 rounded-xl font-medium hover:bg-gray-100 transition-colors"
            >
              Got it
            </button>

            <p className="text-xs text-gray-500 mt-4">
              Works offline • No app store required
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
